import Flow from '@/flows/flow';
import { buildAccountBaseService } from '@/common/service/base-paid';
import PageLoader from '@/common/service/flow/page-loader';
import { computedPbxBasePlans, computedPbxCallingPlans, computedPbxPlans } from '@/common/service/computers/plans/pbx';
import { loadSelectByPersist } from '@/common/service/plan-data-mapper';
import { planTypes, VIEWS, ENUM_UPGRADE_TYPE, APM_PAYMENT_METHODS } from '@/common/constants';
import { addToCart } from '@/common/service/flow/utils';
import { get, has } from 'lodash';
import store from '@/store';
import { updatePlansCheckout, UpdateTypes, getPaymentInfo, addParamsIfApm, addParamsIfReferralProgram, addParamsIfNeedUseVerification, injectOldSubReferId } from '@/common/service/checkout-utils';
import { createCouponPayloadBuilder } from '@/common/service/coupon-payload-builder';
import { getPlansMap } from '@/common/product';
import { addVendorProduct, injectDevicesParams, preSubmitForVendorProduct } from '../helper/vendor-product';
import { freeTrialUpgrade } from '@/apis';
import { pollingEventStatus } from '@/common/polling';
import { gotoSuccessPage, gotoWiseSuccessPage, upgradeSuccessPage } from '@/common/service/common';
import { pageConfig } from '@/common/service/page-config';
import { getOrderSummaryFields } from '@/flows/utils';
import { getFreeTrialUpgradePayloadForZpToProPlus } from '@/flows/services/freetrial-common';

const { APM, WISE } = APM_PAYMENT_METHODS;

/**@start buildShoppingCart */

function loadDefaultSelectByQuery(sourcePlan, boughtPlans) {
  return (planList) => {
    const hasSelected = planList?.find((p) => p.cartInfo.selected_number > 0);
    if (hasSelected) return planList;

    const _pbxCallingBillingCycle = sourcePlan.billing_cycle;
    const _pbxCallingLicense = sourcePlan.cartInfo.selected_number;
    const _pbxCallingRegion = sourcePlan.region;
    const _pbxBoughtBasePlan = computedPbxBasePlans(boughtPlans)[0];

    const _targetPlan = planList.find(item => (
      item.type === planTypes.ZONE_PRO_PLUS &&
      item.region === _pbxCallingRegion &&
      item.billing_cycle === _pbxCallingBillingCycle
    ));

    _targetPlan && addToCart(_targetPlan, _pbxCallingLicense);

    // add pbx base plan to shopping cart
    if (_pbxBoughtBasePlan) {
      const _pbxBasePlan = planList.find(item => item.plan_code === _pbxBoughtBasePlan.plan_code);
      _pbxBasePlan && addToCart(_pbxBasePlan, 1);
    }

    addVendorProduct(planList)
    return planList;
  }
}

async function buildShoppingCart(state) {
  const { persist } = state;
  const { planMap, subscriptions, boughtPlans, freeTrialPlans, allPlanMap } = await buildAccountBaseService();

  // get calling plan
  const [_firstCallingPlan] = computedPbxCallingPlans(boughtPlans);
  if (!_firstCallingPlan) {
    throw new Error('no valid pbx calling plan');
  }

  const loader = new PageLoader(planMap);
  loader.decorate('loadByPersist', loadSelectByPersist(persist));
  loader.decorate('loadDefaultSelectByQuery', loadDefaultSelectByQuery(_firstCallingPlan, boughtPlans));
  const planList = await loader.dispatch();

  return {
    sourcePlan: _firstCallingPlan,
    allPlanMap,
    boughtPlans,
    subscriptions,
    freeTrialPlans,
    planMapFlat: planList,
  }
}
/**@end buildShoppingCart */

/**@start beforeSubmit */

function checkLicense(getters) {
  const sourcePlanLicense = get(getters.sourcePlan, 'cartInfo.selected_number', 1);
  const selectedPlanLicese = get(getters.getSelectedPlans, '0.cartInfo.selected_number', 1);
  if (selectedPlanLicese < sourcePlanLicense) {
    return Promise.reject(`Can not decrease license: souceLicense = ${sourcePlanLicense}, currentLicense = ${selectedPlanLicese}`);
  }
  return Promise.resolve();
}

async function beforeSubmit() {
  if (store.getters.hasZoomDevice) {
    await preSubmitForVendorProduct();
  }
  await checkLicense(store.getters);
}
/**@end beforeSubmit */

/**@start submit */

/**
 * Submit ZP Free Trial upgrade to Pro Plus
 * @param {Object} submitParams - Submit parameters
 * @returns {Promise} Submit result
 */
async function checkOutForZpFreeTrialToProPlus(submitParams) {
  const getters = store.getters;

  // Generate upgrade payload using shared function from freetrial-common
  const { upgradePlanList, addPlanList } = getFreeTrialUpgradePayloadForZpToProPlus(getters);

  const { campaign_code: campaignCode = '', trial_code: trialCode = '' } = pageConfig || {};
  const couponBuilder = createCouponPayloadBuilder(getters);

  const preview = submitParams?.preview ?? false;
  const previewActions = submitParams?.previewActions ?? [];

  let params = {
    upgradeList: injectOldSubReferId(store.getters, upgradePlanList),
    addList: injectOldSubReferId(store.getters, addPlanList),
    coupon: couponBuilder.buildForFreeTrialUpgrade(),
    // FREE TRIAL - online free trial upgrade need 2 new params
    ...(campaignCode ? { campaignCode } : {}),
    ...(trialCode ? { trialCode } : {}),
    ...getPaymentInfo(getters),
    preview,
    previewActions,
    orderSummary: {
      orderSummaryFields: getOrderSummaryFields(store.getters),
    },
  };

  if (!!campaignCode && !!trialCode) {
    params.bizType = ENUM_UPGRADE_TYPE.ONLINE_FREE_TRIAL_UPGRADE;
  }

  params = { ...params, indiaCustomerNonINR: false };
  params.extension = addParamsIfNeedUseVerification(params.extension);
  addParamsIfApm(params);
  addParamsIfReferralProgram(params);

  const result = await freeTrialUpgrade(params);
  const options = preview ? { previewKey: result?.result?.previewKey, rps: true } : { rps: true };
  const eventResult = await pollingEventStatus(options);
  store.commit('setTransactionId', eventResult?.uniqueKey);
  store.commit('setPreviewKey', eventResult?.previewKey);

  const { apmInfo } = store.getters;
  if (apmInfo.isApm) {
    const successParams = {
      integrationType: APM,
      apmMethodName: apmInfo.apmPaymentMethodName,
    };
    if (!preview) {
      apmInfo.apmPaymentMethodName === WISE
        ? gotoWiseSuccessPage({ wiseStateValue: apmInfo.wiseStateValue })
        : gotoSuccessPage(successParams);
    }
    return {
      eventResult,
    };
  }

  if (!preview) {
    upgradeSuccessPage(undefined, undefined, 2000, planTypes.ZONE_PRO_PLUS);
  }
  return {
    eventResult,
  };
}

async function submit(submitParams) {
  const getters = store.getters || {};
  const { freeTrialPlans = [] } = getters;

  // Check if this is a ZP free trial upgrade to Pro Plus
  const zpFreeTrialPlans = computedPbxPlans(freeTrialPlans);
  const isZpFreeTrialUpgrade = zpFreeTrialPlans.length > 0 &&
    zpFreeTrialPlans.every(plan => plan.isOnlineFreeTrial || plan.isFreeTrial);

  // If ZP free trial upgrade, use dedicated flow for ZP FT to Pro Plus
  if (isZpFreeTrialUpgrade) {
    return checkOutForZpFreeTrialToProPlus(submitParams);
  }

  // Otherwise, continue with normal pbx2prop upgrade flow
  const { sourcePlan, orderDataPayload, selectedPlanListPayLoad } = getters;
  const oldPlanCode = sourcePlan?.plan_code;
  const boughtPlanMap = getPlansMap(getters.boughtPlans);

  // Need confirm with back-end teammates.
  const actionType = UpdateTypes.PRO_TO_PTO_PLUS;
  const planList = selectedPlanListPayLoad
  .map(payload => ({ ...payload, oldPlanCode }))
  .filter(payload => !has(boughtPlanMap, payload.planCode))
  .filter(payload => !payload.isVendorProduct);

  const couponData = orderDataPayload?.coupon;
  const extensionUpdateData = {};
  return updatePlansCheckout(
    submitParams,
    planList,
    actionType,
    couponData,
    extensionUpdateData,
    false,
    injectDevicesParams
  );
}
/**@end submit */

export default class extends Flow {
  view() {
    return VIEWS.COMPOUND;
  }
  init() {
    return buildShoppingCart(...arguments);
  }
  beforeSubmit($store) {
    return beforeSubmit($store);
  }
  async submit(submitParams) {
    return submit(submitParams);
  }
}
